/**
 * VẼ ĐƯỜNG ĐẠN CỦA ĐÒN ĐÁNH XA.
 *
 * ⚠️ LỖI NGƯỜI DÙNG BÁO: *"các đòn đánh, skill đánh xa chưa có đường đạn"*.
 * Nguyên nhân đo được: tầng mô phỏng ĐÃ có đạn đầy đủ — `World.projectiles` được đẩy vào ở 3 chỗ
 * (`spawnProjectile` cho đòn thường, `spawnProjectileSpread` cho chiêu ném/ bắn, và nhánh
 * `projectile` của `castSkill`), có `vx/vy/ttl/radius/color/kind` — nhưng **tầng render không có
 * một dòng nào vẽ chúng**. Nên đạn bay, gây sát thương, mà người chơi KHÔNG THẤY GÌ.
 *
 * Cách làm ở đây (không đụng `sim/`):
 *   · 3 loại đạn theo `kind` của `Projectile`: `bullet` (đạn súng), `thrown` (đồ ném: dép, rau,
 *     kẹo, lựu đạn), `word` (chiêu chữ/ tiếng — hiện thành vệt sáng).
 *   · Mỗi đạn = 1 sprite trong POOL dùng lại (không tạo/ huỷ mỗi viên — 60fps, hàng chục viên).
 *   · Kèm VỆT BAY (trail) bằng `TileSprite`? Không — dùng 2 sprite mờ nhỏ phía sau đầu đạn cho rẻ.
 *   · Texture vẽ bằng code (`Graphics.generateTexture`) ⇒ 0 ảnh, 0 hạn ngạch AI, đổi màu theo
 *     `p.color` của từng viên (màu do chiêu/ vũ khí quyết định bên `sim/`).
 */
import Phaser from 'phaser';
import type { Projectile } from '../sim/types';

/**
 * HÌNH RIÊNG CHO TỪNG LOẠI ĐẠN — người dùng: *"kích cỡ đường đạn đang to quá và đang giống nhau,
 * cần custom hình ảnh lại cho đúng với nhân vật"*.
 *
 * MỐC CỠ (để không lặp lại lỗi "đạn to như cái bánh xe"): nhân vật cao **104 đơn vị**.
 *   · đạn súng   ~14×5  đơn vị  (nhỏ, dài, bay nhanh — như vệt lửa)
 *   · đồ ném     ~11×11 đơn vị  (bằng khoảng 1/9 nhân vật — cỡ bàn tay)
 *   · chiêu chữ  ~16×16 đơn vị
 * To hơn nữa là "cục bóng" đè lên nhân vật; nhỏ hơn ~8 đơn vị thì không đọc được trên nền gạch.
 *
 * Mỗi `skin` là MỘT hình khác nhau, vẽ bằng `Graphics.generateTexture` (0 ảnh, 0 hạn ngạch AI):
 *   · `army-basic`   đạn súng vàng, thoi dài
 *   · `street-basic` chiếc dép (kẹp quai) — đúng chất chợ búa
 *   · chiêu của street: `sandalThrow` dép · `veggieBarrage` lá rau · `luckyMoney` bao lì xì
 *   · chiêu của army: `suppressive` đạn · `artillery` quả pháo · `finalSalvo` đạn xuyên phá
 *   · chiêu của agility: `shuriken` cánh hoa · `flurry` chữ "bốp" (khối mềm)
 */
type SkinShape = {
  /** vẽ hình vào graphics (gốc 0,0 ở góc trên-trái khung texture) */
  draw: (g: Phaser.GameObjects.Graphics) => void;
  /** kích thước texture (px vẽ) */
  w: number;
  h: number;
  /** cỡ HIỂN THỊ trên sân, tính theo đơn vị sân */
  dw: number;
  dh: number;
  /** màu mặc định khi `Projectile.color` không hợp lệ */
  fallback: number;
};

/** Vẽ khối tròn có viền tối — dùng cho dép/ rau/ pháo. */
const blob = (g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, squish = 1): void => {
  g.fillStyle(0x1c1917, 0.95);
  g.fillEllipse(cx, cy, (r + 3) * 2, (r + 3) * 2 * squish);
  g.fillStyle(0xffffff, 1);
  g.fillEllipse(cx, cy, r * 2, r * 2 * squish);
};

const SHAPES: Record<string, SkinShape> = {
  // ---------------------------------------------------------------- Bắn thuê
  'army-basic': {
    w: 20, h: 8, dw: 15, dh: 5, fallback: 0xfde68a,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(9, 4, 18, 7);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(9, 4, 15, 4);
    },
  },
  suppressive: {
    w: 20, h: 8, dw: 14, dh: 5, fallback: 0xfde68a,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(9, 4, 18, 6);
      g.fillStyle(0xfff3c4, 1);
      g.fillEllipse(10, 4, 13, 3);
    },
  },
  artillery: {
    w: 18, h: 18, dw: 13, dh: 13, fallback: 0xf97316,
    draw: (g) => {
      blob(g, 9, 9, 6, 1);
      g.fillStyle(0x1c1917, 0.5);
      g.fillEllipse(11, 11, 4, 4);
    },
  },
  finalSalvo: {
    w: 26, h: 8, dw: 19, dh: 5, fallback: 0x60a5fa,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(12, 4, 24, 7);
      g.fillStyle(0xbfdbfe, 1);
      g.fillEllipse(13, 4, 20, 4);
    },
  },
  // ---------------------------------------------------------------- Dân chợ búa
  'street-basic': {
    // CHIẾC DÉP: đế dài + quai ngang — nhìn là biết chợ búa
    w: 22, h: 14, dw: 15, dh: 9, fallback: 0x86efac,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.95);
      g.fillEllipse(11, 8, 20, 11);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(11, 8, 17, 8);
      g.fillStyle(0x1c1917, 0.55);
      g.fillEllipse(9, 6, 9, 3); // quai dép
    },
  },
  sandalThrow: {
    w: 22, h: 14, dw: 15, dh: 9, fallback: 0x86efac,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.95);
      g.fillEllipse(11, 8, 20, 11);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(11, 8, 17, 8);
      g.fillStyle(0x1c1917, 0.55);
      g.fillEllipse(9, 6, 9, 3);
    },
  },
  veggieBarrage: {
    // LÁ RAU: thoi dài nhọn hai đầu
    w: 18, h: 12, dw: 13, dh: 8, fallback: 0x4ade80,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(9, 6, 17, 10);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(9, 6, 14, 7);
      g.fillStyle(0x1c1917, 0.4);
      g.fillEllipse(9, 6, 8, 2);
    },
  },
  muaRauCu: {
    w: 18, h: 12, dw: 13, dh: 8, fallback: 0x4ade80,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(9, 6, 17, 10);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(9, 6, 14, 7);
    },
  },
  nuocMamDoc: {
    // CHAI NƯỚC MẮM: chai nhỏ có cổ
    w: 14, h: 18, dw: 10, dh: 13, fallback: 0xb45309,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillRect(4, 2, 6, 15);
      g.fillStyle(0xffffff, 1);
      g.fillRect(5, 3, 4, 13);
      g.fillStyle(0x1c1917, 0.6);
      g.fillRect(5, 1, 4, 3);
    },
  },
  kinhTe: {
    w: 18, h: 18, dw: 13, dh: 13, fallback: 0xfbbf24,
    draw: (g) => {
      blob(g, 9, 9, 6);
      g.fillStyle(0x1c1917, 0.5);
      g.fillEllipse(9, 9, 5, 5);
    },
  },
  // ---------------------------------------------------------------- Nhà chính
  'core-brick': {
    /*
     * VIÊN GẠCH CỦA NHÀ CHÍNH — người dùng: *"Nhà chính có thể gây dame tầm xa, đường đạn là hình viên
     * gạch, ném theo quỹ đạo vòng cung vào địch"*.
     *
     * Vẽ 3 thứ để mắt đọc ra GẠCH chứ không phải "cục nâu": viền tối, thân đỏ gạch, và HAI ĐƯỜNG VỮA
     * ngang (mạch vữa) — chi tiết duy nhất phân biệt gạch với hòn đá.
     * Cỡ ~15×9 đơn vị (nhân vật 104) — to hơn đạn súng, ngang cỡ bàn tay, khớp độ "nặng" của gạch.
     */
    w: 22, h: 14, dw: 15, dh: 9, fallback: 0xb45309,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.95);
      g.fillRoundedRect(1, 2, 20, 11, 2);
      g.fillStyle(0xffffff, 1);
      g.fillRoundedRect(2, 3, 18, 9, 2);
      g.fillStyle(0x1c1917, 0.45);
      g.fillRect(2, 7, 18, 1); // mạch vữa ngang
      g.fillRect(11, 3, 1, 9); // mạch vữa dọc
    },
  },
  // ---------------------------------------------------------------- Đẻ mướn
  'mother-basic': {
    /*
     * QUẢ TRỨNG GÀ — đòn đánh thường của "Đẻ mướn".
     *
     * Người dùng: *"Đổi class Đẻ mướn ở cả đi ải và arena thành đánh tầm xa. Đường đạn là ném quả
     * trứng gà"*. Vẽ đúng 3 thứ để mắt đọc ra TRỨNG chứ không phải "cục trắng":
     *   · viền tối (nền sân đá sáng nên không có viền là mất hình);
     *   · vỏ trắng + BÓNG ẤM lệch xuống dưới-phải ⇒ thành khối cầu;
     *   · VẾT NỨT zíc-zắc ngang thân — chi tiết duy nhất phân biệt trứng với hòn sỏi.
     * Cỡ ~11×14 đơn vị (nhân vật 104) — ngang cỡ bàn tay, cùng nhóm với dép/ rau củ.
     */
    w: 16, h: 22, dw: 11, dh: 14, fallback: 0xfff8e7,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.95);
      g.fillEllipse(8, 11, 13, 20);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(8, 11, 11, 18);
      g.fillStyle(0xf5d68a, 0.75);
      g.fillEllipse(9, 13, 7, 11);
      g.lineStyle(1.2, 0x1c1917, 0.5);
      g.beginPath();
      g.moveTo(4, 9);
      g.lineTo(7, 11);
      g.lineTo(5, 13);
      g.strokePath();
    },
  },
  // ---------------------------------------------------------------- Gái ngành
  shuriken: {
    // CÁNH HOA: khối mềm hồng
    w: 18, h: 18, dw: 13, dh: 13, fallback: 0xf9a8d4,
    draw: (g) => {
      blob(g, 9, 9, 6);
      g.fillStyle(0x1c1917, 0.45);
      g.fillCircle(9, 9, 2);
    },
  },
  flurry: {
    w: 20, h: 16, dw: 15, dh: 12, fallback: 0xc4b5fd,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(10, 8, 19, 14);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(10, 8, 16, 11);
    },
  },
  // ---------------------------------------------------------------- Ultimate Street
  'xebom-bottle': {
    /*
     * CHAI RƯỢU của ultimate Ném Bom Xăng — người dùng: *"tôi muốn khi dùng skill đó street sẽ ném
     * chai rượu bay theo đường vòng cung"*. Vẽ dáng chai: cổ hẹp + thân phình + GIẺ CHÁY ở nút.
     * Cỡ ~13×18 đơn vị (nhân vật 104) — đủ thấy là chai, không thành "cục bóng".
     */
    w: 16, h: 24, dw: 13, dh: 18, fallback: 0x4ade80,
    draw: (g) => {
      // thân chai (xanh thủy tinh)
      g.fillStyle(0x1c1917, 0.95);
      g.fillRoundedRect(4, 8, 10, 15, 3);
      g.fillStyle(0x86efac, 1);
      g.fillRoundedRect(5, 9, 8, 13, 2);
      // cổ chai
      g.fillStyle(0x1c1917, 0.95);
      g.fillRect(6, 2, 6, 8);
      g.fillStyle(0xbbf7d0, 1);
      g.fillRect(7, 3, 4, 7);
      // giẻ cháy ở nút: lõi vàng + viền cam
      g.fillStyle(0xf97316, 1);
      g.fillTriangle(4, 4, 14, 4, 9, 0);
      g.fillStyle(0xfde68a, 1);
      g.fillTriangle(6, 3, 12, 3, 9, 1);
    },
  },

  // ---------------------------------------------------------------- dự phòng theo `kind`
  bullet: {
    w: 20, h: 8, dw: 15, dh: 5, fallback: 0xfde68a,
    draw: (g) => {
      g.fillStyle(0x1c1917, 0.9);
      g.fillEllipse(9, 4, 18, 7);
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(9, 4, 15, 4);
    },
  },
  thrown: {
    w: 18, h: 18, dw: 12, dh: 12, fallback: 0x86efac,
    draw: (g) => blob(g, 9, 9, 6),
  },
  word: {
    w: 22, h: 22, dw: 16, dh: 16, fallback: 0xa78bfa,
    draw: (g) => {
      g.fillStyle(0xffffff, 0.95);
      g.fillCircle(11, 11, 7);
      g.fillStyle(0xffffff, 0.4);
      g.fillCircle(11, 11, 10);
    },
  },
};

/** Trần sprite trong pool — chặn trường hợp chiêu bắn liên tục làm phình bộ nhớ. */
const MAX = 160;

/**
 * ⚠️⚠️ ĐỘ SÂU LÀ THỨ TỪNG LÀM CẢ TÍNH NĂNG VÔ HÌNH — ĐỌC TRƯỚC KHI SỬA.
 *
 * Sân đấu dùng quy ước **depth theo Y** (nhìn nghiêng, ai đứng thấp hơn thì ở trước):
 * nền -100 · sàn -90 · lưới -80 · Nhà -5 · bóng `y*10 - 1` · **mảnh nhân vật `y*10`** (2000–5800)
 * · thanh máu 60–62 · hiệu ứng 3900+.
 * Đạn đặt `depth = 5` (bản đầu) ⇒ nằm DƯỚI mọi mảnh nhân vật ⇒ đạn bay mà không ai thấy.
 * Nay dùng cùng quy ước với nhân vật, nhích lên 6 để viên đạn bay NGANG QUA người không bị lấp.
 */
const depthFor = (y: number): number => y * 10 + 6;

/** Vệt bay lùi lại 1 đơn vị để không đè lên đầu đạn. */
const TRAIL_DEPTH_OFFSET = -1;

/** Khoá texture cho một skin (`kind` khi không có skin riêng). */
export const texKey = (skin: string): string => `__tdk_proj_${skin}`;

/**
 * KẾT QUẢ PHÂN GIẢI "MỘT VIÊN ĐẠN ⇒ HÌNH ĐỂ VẼ".
 *
 * ⚠️ ĐÂY LÀ ĐƯỜNG DÙNG CHUNG CỦA **CẢ HAI CHẾ ĐỘ CHƠI** (đi ải 10-wave + Arena PVE).
 * Người dùng: *"Trong chế độ chơi arena, hình đường đạn đổi thành hình giống trong chế độ đi ải"*.
 * Cách duy nhất để hai chế độ KHÔNG lệch hình là chỉ có MỘT bảng `SHAPES` và MỘT hàm phân giải —
 * đúng hàm này. Chép bảng/hình sang `render/arena-battle.ts` là tự tạo bản thứ hai rồi lệch.
 */
export interface ShotSkin {
  /** tên skin ĐÃ chọn (skin riêng nếu hợp lệ, không thì lùi về `kind`) */
  name: string;
  /** khoá texture trong TextureManager (dùng cho `setTexture`) */
  texture: string;
  /** cỡ HIỂN THỊ (đơn vị sân) */
  dw: number;
  dh: number;
  /** màu mặc định khi viên đạn không mang `color` hợp lệ */
  fallback: number;
}

/**
 * PHÂN GIẢI `skin`/`kind` → HÌNH ĐỂ VẼ — MỘT CHỖ DUY NHẤT, dùng cho cả 2 chế độ.
 *
 * Quy tắc (giữ nguyên hành vi cũ của `ProjectileLayer`, nay tách ra để Arena dùng lại y hệt):
 *   · `skin` hợp lệ (có trong `SHAPES`) ⇒ dùng skin đó (dép, lá rau, quả pháo, chai rượu…);
 *   · không có/ lạ ⇒ lùi về hình mặc định của `kind` (`bullet` · `thrown` · `word`).
 * `kind` lạ cũng lùi về `thrown` ⇒ thêm class/ chiêu mới không bao giờ vỡ hình.
 */
export function resolveShotSkin(skin: string | undefined, kind: string): ShotSkin {
  const safeKind = kind === 'bullet' || kind === 'word' ? kind : 'thrown';
  const name = skin && SHAPES[skin] ? skin : safeKind;
  const shape = SHAPES[name];
  return { name, texture: texKey(name), dw: shape.dw, dh: shape.dh, fallback: shape.fallback };
}

/**
 * TẠO TEXTURE CHO MỌI SKIN — idempotent (đã có thì bỏ qua), nên gọi bao nhiêu lần cũng được.
 *
 * ⚠️ VÌ SAO TÁCH KHỎI `ProjectileLayer`: Arena cần CÙNG bộ texture nhưng có vòng đời vẽ riêng
 * (đạn Arena là hiệu ứng "vệt bay" ngắn, không phải entity mô phỏng — xem `ArenaShot`). Dùng chung
 * hàm này ⇒ cùng khoá `__tdk_proj_*` trong TextureManager ⇒ **không tạo bản sao texture thứ hai**.
 */
export function ensureProjectileTextures(scene: {
  textures: Phaser.Scene['textures'];
  make: Phaser.Scene['make'];
}): void {
  for (const [name, shape] of Object.entries(SHAPES)) {
    const key = texKey(name);
    if (scene.textures.exists(key)) continue;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.clear();
    shape.draw(g);
    g.generateTexture(key, shape.w, shape.h);
    g.destroy();
  }
}

interface Shot {
  img: Phaser.GameObjects.Image;
  trail: Phaser.GameObjects.Image;
  active: boolean;
}

export class ProjectileLayer {
  private pool: Shot[] = [];
  private used = 0;

  constructor(private scene: Phaser.Scene) {
    ensureProjectileTextures(scene);
  }

  /**
   * Một sprite trong pool (tạo khi cần, giữ lại để tái dùng).
   *
   * ⚠️ `take()` CHỈ được gọi sau khi đầu `render()` đã đặt `active = false` cho TOÀN BỘ pool.
   * Nếu gọi lúc cờ `active` còn sót từ khung trước, nó sẽ nhảy qua slot đang bật và cấp phát slot
   * mới ⇒ slot bị nhảy qua vẫn `visible` nhưng không được vẽ lại ⇒ sprite "ma" kẹt trên màn hình
   * (đúng lỗi người dùng báo). Vòng dọn cuối `render()` nay ẩn theo `visible` nên kể cả khi điều đó
   * xảy ra thì sprite cũng bị tắt ngay trong khung, không tích tụ.
   */
  private take(): Shot | null {
    for (const s of this.pool) if (!s.active) return s;
    if (this.pool.length >= MAX) return null;
    const trail = this.scene.add.image(0, 0, texKey('thrown')).setVisible(false).setAlpha(0.35);
    const img = this.scene.add.image(0, 0, texKey('thrown')).setVisible(false);
    const shot: Shot = { img, trail, active: false };
    this.pool.push(shot);
    return shot;
  }

  /**
   * Vẽ mọi viên đạn đang bay. Gọi mỗi khung hình từ `ArenaScene.update`.
   *
   * `trailLen` tính từ VẬN TỐC chứ không cố định: đạn nhanh (súng) kéo vệt dài, đồ ném chậm thì
   * ngắn — đúng cảm giác chuyển động, và đạn đứng yên (không có) sẽ không có vệt.
   */
  render(projectiles: readonly Projectile[]): void {
    /*
     * ⚠️⚠️ PHẢI TẮT HẾT POOL TRƯỚC KHI LẤY SPRITE — ĐÂY LÀ LỖI TỪNG LÀM ĐẠN "ĐỨNG YÊN".
     *
     * `active` chỉ được tắt ở CUỐI `render()` (đoạn dọn dưới). Nếu để nguyên như vậy thì ở khung
     * sau, `take()` duyệt pool và thấy `pool[0].active === true` ⇒ BỎ QUA nó, tạo sprite MỚI, còn
     * sprite cũ **không bao giờ được cập nhật vị trí nữa**. Đo được (Chrome thật, Dân chợ búa):
     * đạn bay `333 → 324 → …` nhưng sprite cứ đứng ở `333,215` — đúng hiện tượng người dùng báo
     * *"chỉ thấy xuất hiện 1 cục màu xanh ở thân và không thấy gì bay đi cả"*: cục xanh đó là
     * sprite của viên đạn ĐẦU TIÊN bị bỏ quên, còn các viên sau tái dùng sprite mới rồi tắt ngay.
     *
     * Cách đúng: mở đầu mỗi khung, đánh dấu TẤT CẢ là chưa dùng; `take()` cấp phát từ đầu pool;
     * cuối khung tắt những cái vượt quá `used`. Nhờ vậy 1 đạn luôn dùng đúng `pool[0]` mọi khung.
     */
    for (const s of this.pool) s.active = false;
    this.used = 0;
    for (const p of projectiles) {
      const shot = this.take();
      if (!shot) break;
      shot.active = true;
      this.used++;

      /*
       * HÌNH = theo `skin` (class/ chiêu quyết định), KHÔNG theo `kind`.
       * `kind` chỉ là đại loại vật lý; `skin` mới nói viên đạn này là dép · lá rau · đạn · pháo…
       * Việc phân giải nằm ở `resolveShotSkin` — DÙNG CHUNG với Arena (xem chú thích ở đó).
       */
      const shotSkin = resolveShotSkin(p.skin, p.kind);
      const tex = shotSkin.texture;
      const speed = Math.hypot(p.vx, p.vy) || 1;
      const dirX = p.vx / speed;
      const dirY = p.vy / speed;
      const angle = Math.atan2(p.vy, p.vx);

      const disp = { w: shotSkin.dw, h: shotSkin.dh };
      /*
       * ĐƯỜNG VÒNG CUNG — người dùng: *"ném chai rượu bay theo đường vòng cung"*.
       *
       * Mô phỏng chạy trên MẶT PHẲNG (không có trục Z) nên độ cao là chuyện của LỚP VẼ: nâng sprite
       * lên theo `sin(π·arcT)` — 0 ở hai đầu, cao nhất ở giữa ⇒ thành cung parabol.
       * Dùng `-sin` (trục Y màn hình hướng xuống) và nhân 0,75 để đỉnh cung không lệch quá xa vị trí
       * thật (nếu nâng đúng 100% thì mắt thấy chai "bay trên đầu quái" mà va chạm lại ở dưới chân).
       * Thêm hiệu ứng XOAY để chai lộn vòng khi bay, đúng cảm giác ném.
       */
      const arcLift = p.arcHeight ? Math.sin(Math.PI * Math.min(1, Math.max(0, p.arcT ?? 0))) * p.arcHeight * 0.75 : 0;
      const arcSpin = p.arcHeight ? (p.arcT ?? 0) * Math.PI * 2.2 : 0;
      shot.img
        .setTexture(tex)
        .setPosition(p.x, p.y - arcLift)
        .setRotation(angle + arcSpin)
        .setTint(p.color || shotSkin.fallback)
        .setDepth(depthFor(p.y))
        .setVisible(true)
        .setDisplaySize(disp.w, disp.h);

      // VỆT BAY: kéo dài theo tốc độ, nằm sau đầu đạn, mờ và to hơn một chút.
      // VỆT dài hơn hẳn viên đạn ⇒ mắt đọc được "đường bay" chứ không chỉ một chấm.
      const trailLen = Math.max(14, Math.min(46, speed * 0.055));
      /*
       * CHAI RƯỢU KHÔNG có vệt bay: vệt ngang sẽ nằm sai chỗ khi chai đang ở đỉnh cung (nó ở trên
       * cao, vệt lại vẽ dưới thấp) ⇒ nhìn như 2 vật thể rời. Ẩn hẳn, chỉ giữ hình chai + xoay.
       */
      if (p.arcHeight) {
        shot.trail.setVisible(false);
        continue;
      }
      shot.trail
        .setTexture(tex)
        .setPosition(p.x - dirX * trailLen * 0.6, p.y - dirY * trailLen * 0.6)
        .setRotation(angle)
        .setTint(p.color || shotSkin.fallback)
        .setDepth(depthFor(p.y) + TRAIL_DEPTH_OFFSET)
        .setAlpha(0.42)
        .setVisible(true)
        .setDisplaySize(trailLen, disp.h * 0.85);
    }

    /*
     * TẮT phần pool không dùng — KHÔNG huỷ, để lần sau tái dùng.
     *
     * ⚠️⚠️ LỖI ĐÃ DÍNH (người dùng báo: *"đường đạn sau khi trúng không biến mất, nhất là khi tôi
     * tua X3, rất nhiều đường đạn kẹt lại ở màn hình"*).
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: vòng dọn cũ có `if (!s.active) continue;` — tức nó CHỈ tắt sprite nào
     * đang `active`. Nhưng `active` là cờ ĐÃ ĐƯỢC XOÁ ở đầu khung (`for (const s of this.pool)
     * s.active = false;`), rồi `take()` chỉ bật lại cho những slot NÓ CẤP PHÁT. Slot nào bị `take()`
     * BỎ QUA vì đang `!active` ⇒ tới cuối khung vẫn `active === false` NHƯNG `img.visible` vẫn
     * `true` từ khung trước ⇒ dòng `continue` nhảy qua nó ⇒ sprite ở lại màn hình VĨNH VIỄN.
     *
     * Bằng chứng đo thật (Chrome, X3, `sim=5, used=5, pool=6`):
     *   slot 5: { active:false, vis:true, trailVis:true, x:636, y:295 }  ← sprite "ma" còn vẽ
     * Đúng lúc đó `sim.projectiles.length = 0` mà `visibleSprites = 6` ⇒ đo được 94/116 khung bị
     * thừa sprite, có chuỗi **29 khung liên tiếp** `sim=0` mà vẫn còn 6 sprite nằm trên sân.
     *
     * Vì sao X3 làm nặng hơn: tốc độ ×3 ⇒ số đạn sinh/ biến mất mỗi khung nhiều hơn, `used` dao
     * động mạnh (6 → 4 → 1 → 0) nên slot bị bỏ qua xuất hiện thường xuyên hơn hẳn.
     *
     * CÁCH SỬA: bỏ hẳn điều kiện `if (!s.active) continue` và gọi `setVisible(false)` VÔ ĐIỀU KIỆN
     * cho mọi slot `>= used`.
     *
     * ⚠️ VÌ SAO KHÔNG ĐỌC `s.img.visible` ĐỂ "TỐI ƯU": bản đầu tôi viết `if (s.img.visible)
     * s.img.setVisible(false)`. Nó SAI ở 2 điểm:
     *   1. Đọc `.visible` là tin vào trạng thái nội bộ của đối tượng Phaser; sprite giả trong test,
     *      sprite đã `destroy()`, hay sprite bị đụng ở đường khác đều có thể trả `undefined` ⇒ điều
     *      kiện không bao giờ đúng ⇒ CHÍNH NÓ tái tạo lại lỗi "kẹt sprite" mà ta đang sửa.
     *   2. `setVisible(false)` trên sprite đã ẩn là thao tác rẻ và idempotent — "tối ưu" này không
     *      đáng để đánh đổi bằng một lỗi hiển thị.
     * Gọi vô điều kiện vừa đúng vừa kiểm chứng được (test chỉ cần đếm số lần `setVisible`).
     *
     * ⚠️ KHÔNG được quét CẢ pool ở đoạn này: `take()` cấp phát slot `[0, used)` và vòng vẽ phía trên
     * VỪA `.setVisible(true)` cho chúng — quét cả pool sẽ ẩn luôn những sprite vừa vẽ ⇒ đạn tàng
     * hình hoàn toàn. Phạm vi dọn ĐÚNG phải là `[used, pool.length)`.
     *
     * Vì sao vẫn đủ: `take()` luôn lấp slot rảnh ĐẦU TIÊN, nên mọi slot `>= used` chắc chắn không
     * được khung này dùng ⇒ ẩn chúng là đúng trong mọi trường hợp.
     *
     * KHÔNG bật lại gì ở đây: vòng vẽ phía trên đã cố ý ẩn vệt bay của chai rượu (xem `if
     * (p.arcHeight)`), nên việc của đoạn này thuần tuý là DỌN.
     */
    for (let i = this.used; i < this.pool.length; i++) {
      const s = this.pool[i];
      s.active = false;
      s.img.setVisible(false);
      s.trail.setVisible(false);
    }
  }

  /** Số sprite đang dùng — cho bài kiểm và dòng debug. */
  get activeCount(): number {
    return this.used;
  }

  /** Vào màn mới: tắt hết (giữ pool). */
  clear(): void {
    for (const s of this.pool) {
      s.active = false;
      s.img.setVisible(false);
      s.trail.setVisible(false);
    }
    this.used = 0;
  }
}

/** Danh sách skin đang có — cho test khoá "mỗi thứ một hình" và "cỡ phải nhỏ". */
export function SKIN_NAMES(): string[] {
  return Object.keys(SHAPES);
}

/** Cỡ hiển thị (đơn vị sân) của một skin — cho test so với chiều cao nhân vật 104. */
export function SKIN_SIZE(name: string): { dw: number; dh: number } | null {
  const shape = SHAPES[name];
  return shape ? { dw: shape.dw, dh: shape.dh } : null;
}
